import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import UserListWidget from '../../../MultiRole/UserAccessListWidget/UserListWidget';
import MedicAssistantService from '../../../../../API/Services/MedicAssistantService';
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import type { AuthorizedTranslatedUser } from '../../../../../Types/schema-helpers';
import VocabProMenu from '../../../../../ConfigFiles/multilingual/VocabProMenu';

interface AssistantManagementWidgetProps {
  onSaveSuccess?: () => void;
}

export const AssistantManagementWidget: React.FC<AssistantManagementWidgetProps> = ({
  onSaveSuccess,
}) => {
  const { t } = useLanguage();
  const [initialUsers, setInitialUsers] = useState<AuthorizedTranslatedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadAssistants = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await MedicAssistantService.fetchAssistants();
      setInitialUsers(response.authorized_users || []);
    } catch (err) {
      console.error('Error fetching assistants:', err);
      setFetchError(t(VocabProMenu, 'assistants_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAssistants();
  }, [loadAssistants]);

  const handleSearchUser = useCallback(async (query: string): Promise<string | null> => {
    return await MedicAssistantService.searchAssistant(query);
  }, []);

  const handleSave = useCallback(
    async (users: AuthorizedTranslatedUser[]): Promise<void> => {
      await MedicAssistantService.updateAssistants(users);
      setInitialUsers(users);
    },
    []
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <Alert variant="danger" className="m-3">
        {fetchError}
      </Alert>
    );
  }

  return (
    <UserListWidget
      title=""
      subtitle={t(VocabProMenu, 'assistants_desc')}
      authorizedHeader={t(VocabProMenu, 'authorized_assistants')}
      emptyText={t(VocabProMenu, 'no_assistants_found')}
      initialUsers={initialUsers}
      onSearchUser={handleSearchUser}
      onSave={handleSave}
      {...(onSaveSuccess ? { onSaveSuccess } : {})}
    />
  );
};

export default AssistantManagementWidget;
